import React, { useState, useEffect } from 'react';
import { APP_PREFIX } from '../../config/appIdentifiers';

const SPINNER_CLASS_PREFIX = `${APP_PREFIX}-spinner`;
const SPINNER_TEXT_CLASS = `${SPINNER_CLASS_PREFIX}-text`;
const SPINNER_OVERLAY_CLASS = `${SPINNER_CLASS_PREFIX}-overlay`;
const SPINNER_MODAL_CLASS = `${SPINNER_CLASS_PREFIX}-modal`;
const SPINNER_STYLE_ID = `${SPINNER_CLASS_PREFIX}-styles`;

const LoadingSpinner = ({
  type = 'bars',
  size = 'medium',
  color = 'primary',
  text = 'Chargement en cours...',
  showText = true,
  className = '',
  duration = null,
  onComplete = null,
  overlay = false,
  position = 'center',
  inline = false
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Auto-hide after duration
  useEffect(() => {
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        if (onComplete) onComplete();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onComplete]);

  // Inject CSS animations and site fonts
  useEffect(() => {
    const styleId = SPINNER_STYLE_ID;
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes bars {
          0%, 80%, 100% {
            transform: scaleY(0.4);
            opacity: 0.5;
          }
          40% {
            transform: scaleY(1);
            opacity: 1;
          }
        }

        @keyframes dots {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        @keyframes ripple {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          100% {
            transform: scale(4);
            opacity: 0;
          }
        }

        @keyframes scalePulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.7;
          }
        }
        
        /* Styles spécifiques pour les spinners site */
        .${SPINNER_TEXT_CLASS} {
          font-family: 'Roboto', sans-serif;
          font-weight: 500;
          color: var(--gris-fonce);
        }
        
        .${SPINNER_OVERLAY_CLASS} {
          backdrop-filter: blur(2px);
          background: rgba(26, 46, 64, 0.15);
        }
        
        .${SPINNER_MODAL_CLASS} {
          background: white;
          border-radius: 8px;
          padding: 2rem;
          box-shadow: 0 10px 40px rgba(26, 46, 64, 0.2);
          border: 1px solid var(--gris-clair);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Configuration des tailles selon la charte site
  const sizes = {
    bars: {
      small: { width: '2px', height: '16px', gap: '2px' },
      medium: { width: '4px', height: '24px', gap: '4px' },
      large: { width: '6px', height: '40px', gap: '6px' }
    },
    classic: {
      small: { size: '20px', border: '2px' },
      medium: { size: '32px', border: '3px' },
      large: { size: '48px', border: '4px' }
    },
    dots: {
      small: { size: '6px', gap: '4px' },
      medium: { size: '8px', gap: '6px' },
      large: { size: '12px', gap: '8px' }
    },
    pulse: {
      small: { size: '20px' },
      medium: { size: '32px' },
      large: { size: '48px' }
    }
  };

  // Couleurs de la charte site
  const colors = {
    primary: '#4CAF50',  // Vert vitalité
    blue: '#1A2E40',     // Bleu minéral  
    green: '#4CAF50',    // Vert vitalité
    red: '#E53935',      // Rouge alerte
    yellow: '#FFEB3B',   // Jaune énergie
    gray: '#333333'      // Gris foncé
  };

  if (!isVisible) return null;

  // Bars Animation Component - Style site
  const BarsSpinner = () => {
    const config = sizes.bars[size];
    return (
      <div style={{
        display: 'flex',
        gap: config.gap,
        alignItems: 'flex-end',
        height: config.height
      }}>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              width: config.width,
              height: config.height,
              backgroundColor: colors[color],
              borderRadius: '2px',
              animation: 'bars 1.2s infinite ease-in-out',
              animationDelay: `${(i - 2) * 0.12}s`,
              boxShadow: `0 2px 4px rgba(26, 46, 64, 0.1)`
            }}
          />
        ))}
      </div>
    );
  };

  // Classic Spinner Component - Style site
  const ClassicSpinner = () => {
    const config = sizes.classic[size];
    return (
      <div
        style={{
          width: config.size,
          height: config.size,
          border: `${config.border} solid #F4F4F4`,
          borderTop: `${config.border} solid ${colors[color]}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}
      />
    );
  };

  // Dots Animation Component - Style site
  const DotsSpinner = () => {
    const config = sizes.dots[size];
    return (
      <div style={{
        display: 'flex',
        gap: config.gap,
        alignItems: 'center'
      }}>
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            style={{
              width: config.size,
              height: config.size,
              backgroundColor: colors[color],
              borderRadius: '50%',
              animation: 'dots 1.4s infinite ease-in-out both',
              animationDelay: `${(i - 1) * 0.16}s`,
              boxShadow: `0 2px 4px rgba(26, 46, 64, 0.15)`
            }}
          />
        ))}
      </div>
    );
  };

  // Pulse Animation Component - Style site
  const PulseSpinner = () => {
    const config = sizes.pulse[size];
    return (
      <div
        style={{
          width: config.size,
          height: config.size,
          backgroundColor: colors[color],
          borderRadius: '50%',
          animation: 'scalePulse 1.5s ease-in-out infinite',
          boxShadow: `0 4px 12px rgba(26, 46, 64, 0.2)`
        }}
      />
    );
  };

  // Ripple Animation Component - Style site
  const RippleSpinner = () => {
    const config = sizes.classic[size];
    return (
      <div style={{
        position: 'relative',
        width: config.size,
        height: config.size
      }}>
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: `2px solid ${colors[color]}`,
              borderRadius: '50%',
              opacity: 0.75,
              animation: 'ripple 1.2s infinite ease-out',
              animationDelay: `${i * 0.5}s`
            }}
          />
        ))}
      </div>
    );
  };

  // Gradient Spinner Component - Style site
  const GradientSpinner = () => {
    const config = sizes.classic[size];
    const gradientColor = color === 'primary' ? '#4CAF50' : colors[color];
    return (
      <div
        style={{
          width: config.size,
          height: config.size,
          background: `linear-gradient(45deg, ${gradientColor}, ${gradientColor}99)`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          boxShadow: `0 4px 12px rgba(26, 46, 64, 0.2)`
        }}
      />
    );
  };

  // Select spinner type
  const renderSpinner = () => {
    switch (type) {
      case 'bars':
        return <BarsSpinner />;
      case 'classic':
        return <ClassicSpinner />;
      case 'dots':
        return <DotsSpinner />;
      case 'pulse':
        return <PulseSpinner />;
      case 'ripple':
        return <RippleSpinner />;
      case 'gradient':
        return <GradientSpinner />;
      default:
        return <BarsSpinner />;
    }
  };

  // Styles site
  const containerStyle = inline
    ? {
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '6px'
    }
    : {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(26, 46, 64, 0.15)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: position === 'top' ? 'flex-start' : position === 'bottom' ? 'flex-end' : 'center',
    paddingTop: position === 'top' ? '80px' : '0',
    paddingBottom: position === 'bottom' ? '80px' : '0',
    zIndex: 9999
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '8px',
    padding: '2rem',
    boxShadow: '0 10px 40px rgba(26, 46, 64, 0.2)',
    border: '1px solid #F4F4F4',
    maxWidth: '400px',
    width: '90%'
  };

  const textStyle = {
    color: '#333333',
    fontFamily: "'Roboto', sans-serif",
    fontWeight: '500',
    fontSize: '16px',
    textAlign: 'center',
    margin: 0,
    letterSpacing: '0.01em'
  };

  // Spinner content
  const spinnerContent = (
    <div style={containerStyle} className={className}>
      {renderSpinner()}
      {showText && text && (
        <p style={textStyle}>
          {text}
        </p>
      )}
    </div>
  );

  // With overlay (Modal style site)
  if (overlay) {
    return (
      <div style={overlayStyle} className={SPINNER_OVERLAY_CLASS}>
        <div style={modalStyle} className={SPINNER_MODAL_CLASS}>
          {spinnerContent}
        </div>
      </div>
    );
  }

  if (inline) {
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {spinnerContent}
      </div>
    );
  }

  // Without overlay
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      padding: '1rem'
    }}>
      {spinnerContent}
    </div>
  );
};

// Hook adapté pour site
export const useLoadingSpinner = () => {
  const [loading, setLoading] = useState(false);

  const showLoading = (duration = null) => {
    setLoading(true);
    if (duration) {
      setTimeout(() => setLoading(false), duration);
    }
  };

  const hideLoading = () => {
    setLoading(false);
  };

  const withLoading = async (asyncFunction) => {
    setLoading(true);
    try {
      const result = await asyncFunction();
      return result;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    showLoading,
    hideLoading,
    withLoading,
    LoadingSpinner: (props) => loading ? <LoadingSpinner {...props} /> : null
  };
};

export default LoadingSpinner;
